import React, { useEffect, useRef, useState } from "react";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCellProps,
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import moment from "moment";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import ShadowCard from "../../components/common/ShadowCard";
import FormDatePicker from "../../components/formFields/FormDateField";
import {
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import {
  setWalletWithdrawalID,
  setWalletWithdrawalRequestDetails,
} from "./walletWithdrawalSlice";
import {
  getAllWalletWithdrawalRequest,
  updateWithdrawalRequest,
} from "./services/walletWithdrawal.services";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import { FaEye } from "react-icons/fa6";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { checkAcessRights, formatIndianNumber } from "../../_helper/helper";
import { FiEdit } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { EDIT_ACCESS, PAYMENT_STATUS_ARRAY } from "../../_contstants/common";
import { BiCheckDouble } from "react-icons/bi";
import { RxCross2 } from "react-icons/rx";
import { PiClockCountdownFill } from "react-icons/pi";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}
const DummyChangeWatcher: React.FC<FormChangeWatcherProps> = ({
  formRenderProps,
}) => {
  useEffect(() => {
    formRenderProps.onChange("UserID", {
      value: null,
    });
  }, []);

  return null;
};
const WalletWithdrawalRequest: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.walletWithdrawal.loading);
  const WalletWithdrawalRequestDetails = useAppSelector(
    (state) => state.walletWithdrawal.WalletWithdrawalRequestDetails
  );
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  const [initialValue, setInitialValue] = useState<any>({});
  const [formKey, setFormKey] = useState<any>(1);

  useEffect(() => {
    setInitialValue({
      ...WalletWithdrawalRequestDetails,
      FilterFromDate: WalletWithdrawalRequestDetails?.FilterFromDate
        ? moment(
            WalletWithdrawalRequestDetails?.FilterFromDate,
            "YYYY-MM-DD"
          ).toDate()
        : null,
      FilterToDate: WalletWithdrawalRequestDetails?.FilterToDate
        ? moment(
            WalletWithdrawalRequestDetails?.FilterToDate,
            "YYYY-MM-DD"
          ).toDate()
        : null,
    });
    setFormKey(formKey + 1);
  }, [WalletWithdrawalRequestDetails]);

  const handleSubmit = (values: any) => {
    const payload = {
      UserID: null,
      FilterFromDate: values?.FilterFromDate
        ? moment(values?.FilterFromDate).format("YYYY-MM-DD")
        : null,
      FilterToDate: values?.FilterToDate
        ? moment(values?.FilterToDate).format("YYYY-MM-DD")
        : null,
      Limit: +pageLimit,
      Skip: +currentPage * +pageLimit,
    };
    dispatch(
      setWalletWithdrawalRequestDetails({
        FilterFromDate: payload?.FilterFromDate,
        FilterToDate: payload?.FilterToDate,
      })
    );
    dispatch(getAllWalletWithdrawalRequest(payload));
  };

  return (
    <>
      <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
        <GridLayoutItem>
          <ShadowCard>
            <Form
              key={formKey}
              initialValues={initialValue}
              onSubmit={handleSubmit}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement style={{ width: "100%" }}>
                  <GridLayout
                    style={{ marginRight: 30 }}
                    gap={{ rows: 10, cols: 10 }}
                    cols={[
                      { width: "25%" },
                      { width: "25%" },
                      { width: "25%" },
                      { width: "25%" },
                    ]}
                  >
                    <GridLayoutItem colSpan={4}>
                      <Typography.h4
                        style={{ marginBottom: 0, marginLeft: "3px" }}
                      >
                        User Withdrawal Request
                      </Typography.h4>
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="FilterFromDate"
                        label="From Date"
                        format="dd/MM/yyyy"
                        component={FormDatePicker}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="FilterToDate"
                        label="To Date"
                        format="dd/MM/yyyy"
                        minDate={
                          moment(
                            formRenderProps.valueGetter("FilterFromDate")
                          ).toDate() || new Date(1900, 0, 1)
                        }
                        component={FormDatePicker}
                        validator={
                          formRenderProps.valueGetter("FilterFromDate")
                            ? requiredValidator
                            : skipValidator
                        }
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      style={{
                        marginTop: 35,
                      }}
                    >
                      <ButtonWithLoading
                        label={"View"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                  <DummyChangeWatcher formRenderProps={formRenderProps} />
                </FormElement>
              )}
            />
            <WalletWithdrawalRequestGridComponent />
          </ShadowCard>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const WalletWithdrawalRequestGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const WalletWithdrawalRequestList = useAppSelector(
    (state) => state.walletWithdrawal.WalletWithdrawalRequestList
  );
  const loading = useAppSelector((state) => state.walletWithdrawal.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const WalletWithdrawalRequestDetails: any = useAppSelector(
    (state) => state.walletWithdrawal.WalletWithdrawalRequestDetails
  );
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);

  useEffect(() => {
    const payload = {
      ...WalletWithdrawalRequestDetails,
      UserID: null,
      Limit: +pageLimit,
      Skip: +currentPage * +pageLimit,
    };
    dispatch(getAllWalletWithdrawalRequest(payload));
  }, [currentPage, pageLimit]);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleView = (ID: number) => {
      navigate("/walletwithdrawalrequestview", {
        state: {
          ID: ID,
        },
      });
    };
    const handleOpenEditDialog = (ID: number) => {
      dispatch(openDialog("updateWithdrawRequest"));
      dispatch(setWalletWithdrawalID(ID));
    };
    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <>
            <IconButton
              type="button"
              fillMode="flat"
              size="small"
              title="Edit"
              onClick={() => handleOpenEditDialog(props.dataItem.ID)}
            >
              <FiEdit
                className="absolute-position"
                style={{ fontSize: "24px" }}
              />
            </IconButton>
            {/*}  )} */}
            {/*} {isDeleteAccess && ( */}
            <IconButton
              type="button"
              fillMode="flat"
              size="small"
              title="View"
              themeColor="tertiary"
              onClick={() => handleView(props.dataItem.ID)}
            >
              <FaEye
                className="absolute-position"
                style={{ fontSize: "24px" }}
              />
            </IconButton>
          </>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <UpdateWithdrwRequestDialog />
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)", marginTop: 20 }}
        filterable={true}
        filter={filter}
        data={
          filter
            ? filterBy(WalletWithdrawalRequestList, filter)
            : WalletWithdrawalRequestList
        }
        onFilterChange={handleFilterChange}
        onPageChange={pageChange}
        resizable={true}
        skip={currentPage * pageLimit}
        take={pageLimit}
        total={
          WalletWithdrawalRequestList[0]?.TotalRecord ||
          WalletWithdrawalRequestList?.length
        }
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column field="RequestDate" title="Request Date" />
        <Column
          field="first_name"
          title="Name"
          cells={{
            data: (props: any) => {
              return (
                <td>
                  {props.dataItem.first_name} {props.dataItem.last_name}
                </td>
              );
            },
          }}
        />
        <Column
          field="Amount"
          title="Amount"
          cells={{
            data: (props: any) => {
              return (
                <td className="text-end">
                  {props.dataItem.Amount
                    ? formatIndianNumber(props.dataItem.Amount)
                    : ""}
                </td>
              );
            },
          }}
        />
        <Column field="Remarks" title="Remarks" />
        <Column
          field="WithdrawlStatus"
          title="Withdrawal Status"
          width={150}
          cells={{
            data: (props: any) => {
              return (
                <td
                  style={{
                    ...props.style,
                  }}
                >
                  <div
                    style={{
                      padding: "5px 2px",
                      background:
                        props.dataItem.WithdrawlStatus === "Pending"
                          ? "goldenrod"
                          : props.dataItem.WithdrawlStatus === "Rejected"
                          ? "lightcoral"
                          : "#00a34de6",
                      color: "white",
                      borderRadius: "30px",
                      width: "120px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 5,
                    }}
                  >
                    {props.dataItem.WithdrawlStatus === "Pending" ? (
                      <PiClockCountdownFill fontSize={18} />
                    ) : props.dataItem.WithdrawlStatus === "Rejected" ? (
                      <RxCross2 fontSize={18} />
                    ) : (
                      <BiCheckDouble fontSize={18} />
                    )}

                    {props.dataItem.WithdrawlStatus}
                  </div>
                </td>
              );
            },
          }}
        />
        <Column field="WithdrawDate" title="Withdraw Date" />
        {/* {(isEditAccess || isDeleteAccess) && ( */}
        <Column
          field="ID"
          title="Actions"
          cells={{ data: MyEditCommandCell }}
          width={"110px"}
          filterable={false}
        />
        {/* )} */}
      </KendoGrid>
    </>
  );
};

const UpdateWithdrwRequestDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const WalletWithdrawalID = useAppSelector(
    (state) => state.walletWithdrawal.WalletWithdrawalID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const loading = useAppSelector((state) => state.walletWithdrawal.loading);
  const WalletWithdrawalRequestDetails: any = useAppSelector(
    (state) => state.walletWithdrawal.WalletWithdrawalRequestDetails
  );
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  const handleSubmit = async (values: any) => {
    try {
      const editPayload = {
        ID: WalletWithdrawalID,
        PaymentDate: values?.PaymentDate
          ? moment(values?.PaymentDate).format("YYYY-MM-DD")
          : null,
        Remarks: values?.Remarks,
        Status: values?.Status,
      };
      const response = await dispatch(updateWithdrawalRequest(editPayload));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(closeDialog());
        const payload = {
          ...WalletWithdrawalRequestDetails,
          UserID: null,
          Limit: +pageLimit,
          Skip: +currentPage * +pageLimit,
        };
        dispatch(getAllWalletWithdrawalRequest(payload));
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {dialogName === "updateWithdrawRequest" && (
        <AppDialog>
          <>
            {loading && <LoadingPanel gridRef={gridRef} />}
            <Typography.h5>{"Update Withdraw Request"}</Typography.h5>
            <Form
              onSubmit={handleSubmit}
              initialValues={{}}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement style={{ width: "500px" }}>
                  <GridLayout
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                    }}
                    gap={{ rows: 10 }}
                    cols={[{ width: "100%" }]}
                  >
                    <GridLayoutItem style={{ width: "100%" }}>
                      <Field
                        id={"Status"}
                        name={"Status"}
                        component={FormSelectionField}
                        label={"Status"}
                        options={PAYMENT_STATUS_ARRAY?.map((status: any) => {
                          return {
                            value: status.value,
                            label: status.label,
                          };
                        })}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem style={{ width: "100%" }}>
                      <Field
                        id={"PaymentDate"}
                        name={"PaymentDate"}
                        component={FormDatePicker}
                        label={"Payment Date"}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem style={{ width: "100%" }}>
                      <Field
                        name="Remarks"
                        label="Remarks"
                        placeholder="Remarks"
                        component={FormTextArea}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      colSpan={1}
                      style={{
                        textAlign: "end",
                        marginTop: "10px",
                        width: "100%",
                      }}
                    >
                      <DialogCloseButton themeColor="primary" />
                      <ButtonWithLoading
                        label={"Update"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </FormElement>
              )}
            />
          </>
        </AppDialog>
      )}
    </>
  );
};

export default WalletWithdrawalRequest;
